/**
 * What a trace of a dashboard table calls the names the dashboard's own
 * scripts make: the filter universes (`universeComposer`), a tile's rows and
 * columns temporaries and its result table (`tileScript`). Pure.
 */

import type { DashboardDocument } from "features/Dashboard/types/dashboardDocument"
import { tileTableId } from "./tileScript"

/** Each tile's result table name with the tile's title ("" when it has none). */
const tileTables = (document: DashboardDocument): Map<string, string> => {
  const m = new Map<string, string>()
  document.pages.forEach((page) =>
    page.tiles.forEach((tile) => m.set(tileTableId(tile.id), tile.title?.trim() ?? ""))
  )
  return m
}

/** The title of the tile whose table (or a panel's, `t_<tile>_<k>`) is `table`; null when no tile has it. */
const titleOf = (tables: Map<string, string>, table: string): string | null => {
  const own = tables.get(table)
  if (own !== undefined) return own
  const panel = /^(.*)_\d+$/.exec(table)
  return panel ? (tables.get(panel[1]) ?? null) : null
}

const ofTile = (what: string, title: string | null): string =>
  title ? `${what} of "${title}"` : `${what} of a tile`

/**
 * The description of `name`, a dashboard helper or tile table, or
 * undefined for any other name.
 */
export const describeDashboardHelper = (
  document: DashboardDocument
): ((name: string) => string | undefined) => {
  const tables = tileTables(document)
  return (name) => {
    if (name.startsWith("__dq_f_")) return "Dashboard filter"
    if (name.startsWith("__dq_c_")) return "Dashboard filter conditions"
    if (name.startsWith("__dq_r_")) return ofTile("Rows", titleOf(tables, name.slice("__dq_r_".length)))
    if (name.startsWith("__dq_e_")) return ofTile("Columns", titleOf(tables, name.slice("__dq_e_".length)))
    if (name.startsWith("__dq_")) return "Dashboard helper"
    const title = titleOf(tables, name)
    if (title !== null) return title ? `Table of "${title}"` : "Table of a tile"
    return undefined
  }
}

/** The name of a script that writes another tile's table: that tile's script. */
export const describeDashboardScript = (
  document: DashboardDocument
): ((script: string) => string | undefined) => {
  const tables = tileTables(document)
  return (script) => {
    for (const m of script.matchAll(/^\s*(?:XTAB|DTAB)\s+(\S+)/gim)) {
      const title = titleOf(tables, m[1])
      if (title !== null) return ofTile("Script", title)
    }
    return undefined
  }
}
